import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { DistrictMapPmtiles } from '@citizen-walk/shared-types';

/**
 * PMTiles オフラインマップ キャッシュ層 (Phase 5.3 R-33 step 5、v1 踏襲)。
 *
 * 旧 `old/apps/user/src/lib/offlineMap.ts` の IndexedDB schema (`offlineMaps` store、
 * key=districtId) をそのまま踏襲。Blob 永続化 + storage estimate 表示用。
 *
 * 提供 API:
 *   - `getOfflineMapEntry(districtId)`: cache lookup (Blob + metadata)
 *   - `putOfflineMapEntry(entry)`: download 完了後の永続化
 *   - `deleteOfflineMapEntry(districtId)`: cache 削除 (容量管理 UI 用)
 *   - `listOfflineMapEntries()`: 全 entry 列挙 (admin 確認 / debug 用)
 *   - `downloadPmtilesBlob(meta, opts)`: Storage URL → Blob (progress callback あり)
 *
 * 未実装 (Phase 5 後続 R-33):
 *   - LRU 自動退避 (現状は手動削除のみ)
 *   - Partial download / Resume (旧 v1 も全 Blob DL のみ)
 *   - Service Worker での network fallback (現状は IndexedDB 直接)
 */

export interface OfflineMapEntry {
  /** PMTiles の Storage path (district.mapPmtiles.storagePath と一致)。cache-hit 判定 key。 */
  storagePath: string;
  /** 描画スキーマ (旧と同じ、'protomaps' default)。 */
  schema: 'protomaps' | 'omt';
  /** PMTiles Blob 本体。 */
  blob: Blob;
  /** ファイルサイズ (bytes、UI 表示用)。 */
  sizeBytes: number;
  /** タイル最大 zoom (14|15|16)。district.mapPmtiles.maxZoom と一致。 */
  maxZoom: 14 | 15 | 16;
  /** PMTiles 生成日時 (ISO8601、district.mapPmtiles.generatedAt と一致)。 */
  generatedAt: string;
  /** user がローカル DL 完了した日時 (ISO8601、cache 鮮度判定)。 */
  downloadedAt: string;
}

interface OfflineMapDBSchema extends DBSchema {
  offlineMaps: {
    key: string; // districtId
    value: OfflineMapEntry;
  };
}

const DB_NAME = 'citizen-walk-offline-maps';
const DB_VERSION = 1;
const STORE = 'offlineMaps';

let dbPromise: Promise<IDBPDatabase<OfflineMapDBSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<OfflineMapDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineMapDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

export async function getOfflineMapEntry(districtId: string): Promise<OfflineMapEntry | undefined> {
  const db = await getDb();
  return db.get(STORE, districtId);
}

export async function putOfflineMapEntry(
  districtId: string,
  entry: OfflineMapEntry,
): Promise<void> {
  const db = await getDb();
  await db.put(STORE, entry, districtId);
}

export async function deleteOfflineMapEntry(districtId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, districtId);
}

export async function listOfflineMapEntries(): Promise<
  Array<{ districtId: string; entry: OfflineMapEntry }>
> {
  const db = await getDb();
  const tx = db.transaction(STORE, 'readonly');
  const store = tx.objectStore(STORE);
  const keys = (await store.getAllKeys()) as string[];
  const values = await store.getAll();
  const result: Array<{ districtId: string; entry: OfflineMapEntry }> = [];
  for (let i = 0; i < keys.length; i += 1) {
    const entry = values[i];
    if (entry) result.push({ districtId: keys[i]!, entry });
  }
  return result;
}

/**
 * cache-hit 判定 (storagePath + generatedAt の一致で判定、旧 v1 踏襲)。
 *   - cache あり + 一致 → cache を返却 (再 DL 不要)
 *   - cache あり + 不一致 → 古い cache を破棄して null 返却 (新規 DL 必要)
 *   - cache なし → null 返却
 */
export async function lookupValidCache(
  districtId: string,
  meta: DistrictMapPmtiles,
): Promise<OfflineMapEntry | null> {
  const cached = await getOfflineMapEntry(districtId);
  if (!cached) return null;
  const expectedSchema = meta.schema ?? 'protomaps';
  if (cached.storagePath === meta.storagePath && cached.schema === expectedSchema) {
    return cached;
  }
  // 古い cache を破棄 (admin が新版を生成した場合等)
  await deleteOfflineMapEntry(districtId);
  return null;
}

/**
 * Firebase Storage download URL から PMTiles Blob を取得 (progress callback 付き、v1 fetchWithProgress 踏襲)。
 *
 * @param downloadUrl `getDownloadURL()` で取得した署名付き URL (Storage rules で読取権限済の前提)
 * @param onProgress 進捗 callback (loaded / total bytes)
 * @returns Blob (PMTiles バイナリ)
 */
export async function fetchPmtilesBlobWithProgress(
  downloadUrl: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<Blob> {
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error(`PMTiles fetch failed: ${res.status} ${res.statusText}`);
  }
  const totalHeader = res.headers.get('content-length');
  const total = totalHeader ? parseInt(totalHeader, 10) : 0;
  if (!res.body) {
    // body stream 未対応 (古い browser) → 単純取得 + progress 未発火
    return res.blob();
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.length;
      onProgress?.(loaded, total);
    }
  }
  return new Blob(chunks as BlobPart[], { type: 'application/octet-stream' });
}

/**
 * bytes を人間可読 (B / KB / MB / GB) に整形 (R-33 Sprint W, MapDownloadPage と共通利用)。
 *   - MapPage の PMTiles cache 状態表示でも再利用 (Sprint W、2026-05-19)
 *   - 表示 only、丸めは小数 1〜2 桁
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * navigator.storage.estimate() ラッパ (UI 表示用、旧 v1 踏襲)。
 *   - 未対応 browser では `null` を返す
 */
export async function getStorageEstimate(): Promise<{
  usageBytes: number;
  quotaBytes: number;
} | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  const est = await navigator.storage.estimate();
  return {
    usageBytes: est.usage ?? 0,
    quotaBytes: est.quota ?? 0,
  };
}
